import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import styles from '../../Distributor.module.scss';
import classNames from 'classnames/bind';
import { handleApiError } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';
import utilityApi from '~/apis/utility';
import BaseRow from '~/components/common/base-row/BaseRow';
import ConfirmDistributorDialog from './ConfirmDistributorDialog';
import { IAlert } from '~/utils/interface/alert';

const cx = classNames.bind(styles);

type TransferDistributorDialogProps = {
  open: boolean;
  data?: IAlert;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
};

const TransferDistributorDialog = (props: TransferDistributorDialogProps) => {
  const { t } = useTranslation(['admin/distributor', 'admin/common']);

  const [distributorOptions, setDistributorOptions] = useState<IMasterDataItem[]>([]);
  const [username, setUsername] = useState<string>('');

  const [touched, setTouched] = useState(false);
  const [openConfirmDistributorDialog, setOpenConfirmDistributorDialog] = useState<boolean>(false);

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          const response = await utilityApi.getListDistributors();
          setDistributorOptions(
            response.data.map((item) => {
              return {
                id: item.username,
                name: item.username + ' - ' + item.name,
                data0: item.name,
              };
            })
          );
        } catch (error) {
          handleApiError(error);
        }
      })();
    }
  }, [props.open]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    props.setOpen(open, needRefreshData);
  };

  const handleClickConfirmation = () => {
    setTouched(true);
    if (username) {
      setOpenConfirmDistributorDialog(true);
    }
  };

  return (
    <Box className='w-full'>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle className='bg-[#ED1002] text-center'>{t('transfer.title')}</DialogTitle>
        <DialogContent className={cx('transfer-container')}>
          <BaseRow className='!flex-col !items-center'>
            <Typography className='mb-3.5'>{t('transfer.select')}</Typography>
            <Box className='w-2/3'>
              <BaseSelect
                options={distributorOptions}
                value={username}
                placeholder={t('transfer.selectPlaceholder')}
                isSearchEnable
                onChange={(e) => setUsername(e.target.value)}
                error={touched && Boolean(username === '')}
                helperText={touched && username.trim() === '' ? t('transfer.select') : ''}
              />
            </Box>
          </BaseRow>
        </DialogContent>
        <DialogActions className='justify-evenly'>
          <Button variant='outlined' color='secondary' className='min-w-[125px]' onClick={() => handleSetOpen(false)}>
            {t('cancel', { ns: 'admin/common' })}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            className='min-w-[125px] ml-auto'
            onClick={() => handleClickConfirmation()}
          >
            {t('confirm', { ns: 'admin/common' })}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDistributorDialog
        open={openConfirmDistributorDialog}
        alertUsername={props.data?.username}
        username={username}
        setOpen={setOpenConfirmDistributorDialog}
        setOpenTransferDialog={props.setOpen}
      />
    </Box>
  );
};

export default memo(TransferDistributorDialog);
